import React, { FC } from 'react';

import { Button } from 'antd';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    CloseOutlined,
    DeleteOutlined,
    LoadingOutlined,
    RedoOutlined,
} from '@ant-design/icons';
import type { UploadFileStatus } from 'antd/lib/upload/interface';
import { useTranslation } from 'react-i18next';

import { bytesToText } from '../../utils';
import styles from './MyFilesUploadPopup.module.scss';

const FULL_PERCENTS = 100;
const nameSpace = 'uploadFiles.myFilesPopup';

interface UploadRowProps {
    name: string;
    size: number;
    percent?: number;
    status: UploadFileStatus;
    subText?: string;
    onCancel: () => void;
    onDelete: () => void;
    onRetry: () => void;
}

const UploadRow: FC<UploadRowProps> = ({
    name,
    size,
    percent,
    status,
    subText,
    onCancel,
    onDelete,
    onRetry,
}) => {
    const { t } = useTranslation();

    const isUploading = status === 'uploading';
    const isDone = status === 'done';
    const isError = status === 'error';

    const uploadedBytes = Math.floor((size * (percent || 0)) / FULL_PERCENTS);
    const defaultSub = isUploading
        ? t(`${nameSpace}.progress`, { uploaded: bytesToText(uploadedBytes), total: bytesToText(size) })
        : bytesToText(size);

    return (
        <div className={styles.row} data-testid="myFilesUploadPopup_row">
            <div className={styles.info}>
                <span className={styles.name} title={name}>{name}</span>
                <span className={styles.sub}>{subText ?? defaultSub}</span>
            </div>
            <div className={styles.actions}>
                {isUploading && <LoadingOutlined className={styles['status-uploading']} spin />}
                {isDone && <CheckCircleFilled className={styles['status-done']} />}
                {isError && <CloseCircleFilled className={styles['status-error']} />}

                {isError && (
                    <Button
                        type="link"
                        size="small"
                        className={styles['retry-link']}
                        icon={<RedoOutlined />}
                        onClick={onRetry}
                        data-testid="myFilesUploadPopup_retry"
                    />
                )}

                {isDone ? (
                    <Button
                        type="text"
                        size="small"
                        className={styles['icon-btn']}
                        icon={<DeleteOutlined />}
                        onClick={onDelete}
                        data-testid="myFilesUploadPopup_delete"
                    />
                ) : (
                    <Button
                        type="text"
                        size="small"
                        className={styles['icon-btn']}
                        icon={<CloseOutlined />}
                        onClick={onCancel}
                        data-testid="myFilesUploadPopup_cancel"
                    />
                )}
            </div>
        </div>
    );
};

export default UploadRow;
